import React, { useState, useEffect } from 'react'
import { Form, Button, Alert } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { login } from '../../store/slices/authSlice'
import { useNavigate, Link } from 'react-router-dom'
import { BiInfoCircle, BiShow, BiHide, BiLockAlt, BiUser } from 'react-icons/bi'
import BrandLogo from '../../components/brand/BrandLogo'
import dhruvLogo from '../../assets/dhruv.png'
import { isDhruvDeployment } from '../../config/deploymentProfile'
import { documentTitle, WORKSPACE_LABEL, COMPANY_SHORT_NAME, WORKSPACE_TAGLINE } from '../../config/branding'
import { platformAPI } from '../../services/platformAPI'
import { applyBrandColours } from '../../utils/brandColours'

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [brand, setBrand] = useState(null)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading, error } = useSelector(state => state.auth)
  const dhruvWorkspace = isDhruvDeployment()

  useEffect(() => {
    if (!dhruvWorkspace) return undefined
    document.title = documentTitle(WORKSPACE_LABEL)
    document.body.classList.add('workspace-theme')
    let cancelled = false
    platformAPI.publicBranding('DCSL')
      .then((response) => {
        if (cancelled) return
        const next = response.data?.data
        if (!next) return
        setBrand(next)
        applyBrandColours(next.primaryColor, next.secondaryColor)
        if (next.displayName) document.title = `${next.displayName} | ${WORKSPACE_LABEL}`
      })
      .catch(() => {})
    return () => {
      cancelled = true
      document.body.classList.remove('workspace-theme')
    }
  }, [dhruvWorkspace])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      const payload = dhruvWorkspace
        ? { ...formData, companyCode: 'DCSL' }
        : formData
      const result = await dispatch(login(payload)).unwrap()
      const kind = result?.data?.user?.company?.organizationKind || 'buyer'
      const postLogin = result?.data?.user?.postLoginRoute
      if (postLogin) navigate(postLogin)
      else if (kind === 'supplier') navigate('/respond/inbox')
      else navigate(dhruvWorkspace ? '/workspace' : '/dashboard')
    } catch {
      // error shown via redux
    }
  }

  if (dhruvWorkspace) {
    const copyrightYear = new Date().getFullYear()

    return (
      <div className="auth-page workspace-auth workspace-auth--simple">
        <div className="workspace-login-scene" aria-hidden="true">
          <div className="workspace-login-scene__watermark" />
          <div className="workspace-login-scene__accent" />
        </div>
        <div className="workspace-login-container">
          <div className="workspace-login-card">
            <div className="workspace-login-brand">
              <img
                src={brand?.logoUrl || dhruvLogo}
                className="workspace-login-logo"
                alt={brand?.displayName || COMPANY_SHORT_NAME}
                decoding="async"
                onError={(event) => {
                  event.currentTarget.onerror = null
                  event.currentTarget.src = dhruvLogo
                }}
              />
            </div>

            <h1 className="workspace-login-title">{brand?.loginLine || `Welcome to ${COMPANY_SHORT_NAME}`}</h1>
            <p className="workspace-login-tagline">{brand?.tagline || WORKSPACE_TAGLINE}</p>

            {error && (
              <Alert variant="danger" className="error-alert workspace-login-alert">
                {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit} className="auth-form workspace-login-form">
              <Form.Group className="form-group">
                <Form.Label className="form-label">Email or User ID</Form.Label>
                <div className="auth-input-shell">
                  <span className="auth-input-icon auth-input-icon--leading" aria-hidden="true">
                    <BiUser size={18} />
                  </span>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    className="form-control auth-input-with-icons"
                    required
                    autoComplete="username"
                  />
                </div>
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label className="form-label">Password</Form.Label>
                <div className="auth-input-shell">
                  <span className="auth-input-icon auth-input-icon--leading" aria-hidden="true">
                    <BiLockAlt size={18} />
                  </span>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    className="form-control auth-input-with-icons"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="auth-input-icon auth-input-icon--trailing password-visibility-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <BiHide size={20} /> : <BiShow size={20} />}
                  </button>
                </div>
              </Form.Group>

              <div className="workspace-login-checks">
                <Form.Check
                  type="checkbox"
                  id="show-password"
                  label="Show Password"
                  className="form-check"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                />
                <Form.Check type="checkbox" id="remember-me-dhruv" label="Remember Me" className="form-check" />
              </div>

              <Button type="submit" className="auth-button workspace-login-submit" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign In'}
              </Button>
            </Form>
          </div>
        </div>

        <p className="workspace-login-copyright">
          © {copyrightYear} Celeris Venture Systems Pvt. Ltd. All rights reserved.
        </p>
      </div>
    )
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="brand-content">
              <div className="brand-logo">
                <div className="logo-icon">T</div>
              </div>
              <h1 className="brand-title">Tender360</h1>
              <p className="brand-subtitle">AI Tender Intelligence & Opportunity Management</p>

              <ul className="brand-features">
                  {[
                    'AI-Powered Tender Intelligence',
                    'Advanced Document Management',
                    'Real-time Analytics & Reporting',
                    'Compliance & Risk Management'
                  ].map((label) => (
                    <li key={label}>
                      <div className="feature-icon">✓</div>
                      <span>{label}</span>
                    </li>
                  ))}
                </ul>

              <div className="brand-actions">
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm"
                    onClick={() => window.open('/about', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes')}
                    style={{ fontSize: '0.85rem', padding: '0.5rem 1rem', marginTop: '1.5rem' }}
                  >
                    <BiInfoCircle className="me-1" />
                    About Tender360
                  </button>
                </div>
            </div>
          </div>

          <div className="auth-form">
            <div className="form-header">
              <h2 className="form-title">Welcome back</h2>
              <p className="form-subtitle">Sign in to your Tender360 workspace</p>
            </div>

            {error && (
              <Alert variant="danger" className="error-alert">
                {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="form-group">
                <Form.Label className="form-label">Email Address</Form.Label>
                <div className="auth-input-shell">
                  <span className="auth-input-icon auth-input-icon--leading" aria-hidden="true">
                    <BiUser size={18} />
                  </span>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className="form-control auth-input-with-icons"
                    required
                    autoComplete="username"
                  />
                </div>
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label className="form-label">Password</Form.Label>
                <div className="auth-input-shell">
                  <span className="auth-input-icon auth-input-icon--leading" aria-hidden="true">
                    <BiLockAlt size={18} />
                  </span>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    className="form-control auth-input-with-icons"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="auth-input-icon auth-input-icon--trailing password-visibility-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <BiHide size={20} /> : <BiShow size={20} />}
                  </button>
                </div>
              </Form.Group>

              <div className="form-options">
                <Form.Check
                  type="checkbox"
                  id="remember-me"
                  label="Remember me"
                  className="form-check"
                />
                <a href="/forgot-password" className="forgot-password">
                  Forgot password?
                </a>
              </div>

              <Button
                type="submit"
                className="auth-button"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
              <p className="text-center mt-3 mb-0 small">
                <Link to="/register-respondent">Create participant account</Link>
              </p>
            </Form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
